/*
 * Lot P, F1 (26/10/2026) : initialisation différée des composants ClrzApp situés loin sous le premier écran.
 *
 * Avant : ClrzApp.init (assets/bundle.js) construit tous les composants au DOMContentLoaded, puis bundle.js appelle
 * tous leurs onReady dans la même tâche (montage Splide des carrousels, lecture des styles des accordéons...).
 * Mesure sur l'accueil à 390 px : environ 40 ms sans bridage, soit environ 160 ms avec un processeur 4 fois plus lent.
 *
 * Maintenant : même appel ClrzApp.init, sans modifier bundle.js. Pendant cet appel, la liste renvoyée à ClrzApp pour
 * les sélecteurs de LAZY ne contient que les éléments visibles dans le premier écran (ou masqués : ils gardent le
 * comportement d'origine). Les autres sont construits plus tard, un par un, par le même ClrzApp.init limité à cet
 * élément : à l'approche de l'écran (IntersectionObserver, 800 px d'avance) ou pendant le temps libre après le
 * chargement. Les tableaux ClrzApp.Slider, ClrzApp.Expandable... gardent tous leurs éléments.
 *
 * Non différés : bandeaux défilants (refonte-a11y.js y pose le bouton pause au chargement), en-tête, menus, panier,
 * fiche produit, parallaxe GSAP.
 * Sans IntersectionObserver ou en cas d'erreur : ClrzApp.init() d'origine, inchangé.
 *
 * v2 (agent-F1, 26/10/2026 soir) :
 * 1. Un composant différé est construit sans refaire les 24 autres recherches dans la page (v1 : 25 appels
 *    document.querySelectorAll sur tout le document par composant, dont 24 au résultat jeté).
 * 2. File d'attente : un seul composant par tâche (setTimeout 0 à l'approche de l'écran, temps libre ensuite). En v1,
 *    tous les composants situés entre 200 et 800 px sous l'écran étaient construits dans le même rappel de
 *    l'IntersectionObserver, ce qui pouvait recréer une tâche longue.
 * 3. Filet de sécurité : un pointeur, un toucher ou le focus clavier qui arrive dans un composant pas encore construit
 *    le construit tout de suite (phase de capture), avant que l'événement n'atteigne ses éléments.
 * 4. Bandeaux défilants (barre d'annonce, pied de page) : défilement Splide AutoScroll suspendu tant que le bandeau est
 *    hors de l'écran. À chaque image, AutoScroll relit la position des diapositives en boucle (getBoundingClientRect et
 *    getComputedStyle) : ce travail s'arrête quand personne ne voit le bandeau. Reprise 64 px avant qu'il ne redevienne
 *    visible : rien ne change à l'écran. Une pause voulue (bouton pause de refonte-a11y.js : aria-pressed="true", y
 *    compris avec la préférence « réduire les animations » ; survol ; focus dans le bandeau) n'est jamais levée ici, et
 *    elle est réappliquée à l'événement load, où AutoScroll se relance de lui-même (option autoStart).
 * 5. Carrousel construit après coup : Splide lui donne role="region" sans nom. refonte-a11y.js (section 7,
 *    labelCarousels) le nomme quand des éléments sont ajoutés à la page (MutationObserver) ; on le lui signale par un
 *    élément vide ajouté puis retiré aussitôt (même nommage qu'au chargement, aucune règle dupliquée ici).
 */
(function () {
  'use strict';
  if (window.scClrzLazyInit) return;

  var LAZY = ['.js-slider', '.js-expandable'];
  var SELECTORS = ['.js-header', '.js-nav', '.js-nav-submenu', '.js-expandable', '.js-expand', '.js-link-select',
    '.js-announce-bar', '.js-footer-slider', '.js-slider', '.js-filters-wrapper', '.js-parallax',
    '.js-fixed-text-container', '.js-mini-cart-wrapper', '.js-sidebar', '.js-product', '.js-sizeguide', '.js-search',
    '.js-youtube-player', '.js-aov', '.js-video-player', '.js-redirect', '.js-extend-href', '.js-newsletter',
    '.js-quickbuy-init', '.js-hide-scrollbar'];
  var BELOW = 200;      // px sous le bas du premier écran à partir desquels un composant est différé
  var AHEAD = '0px 0px 800px 0px';
  var MARQUEE_MARGIN = '64px 0px 64px 0px';
  var USER_EVENTS = ['pointerdown', 'touchstart', 'focusin'];
  var pending = [];
  var queue = [];
  var io = null;
  var idleMode = false;
  var soonScheduled = false;
  var idleScheduled = false;
  var listening = false;

  function logError(e) {
    if (window.console && console.error) console.error(e);
  }

  function farBelow(el) {
    if (!el.getClientRects().length) return false; // masqué : comportement d'origine
    return el.getBoundingClientRect().top > (window.innerHeight || document.documentElement.clientHeight) + BELOW;
  }

  // Appelle ClrzApp.init. Pour ses 25 sélecteurs, document.querySelectorAll renvoie ce que choose(s, read) décide ;
  // read() fait la vraie recherche et n'est appelée que si choose en a besoin.
  function runInit(choose) {
    var D = document;
    var native = Document.prototype.querySelectorAll;
    D.querySelectorAll = function (s) {
      if (SELECTORS.indexOf(s) === -1) return native.call(D, s);
      return choose(s, function () { return native.call(D, s); });
    };
    try {
      window.ClrzApp.init();
    } finally {
      delete D.querySelectorAll;
    }
  }

  function ping() {
    var b = document.body;
    if (!b) return;
    var s = document.createElement('span');
    s.hidden = true;
    s.setAttribute('data-sc-lazy-ping', '');
    b.appendChild(s);
    b.removeChild(s);
  }

  function unnamedRegion(el) {
    return el.getAttribute('role') === 'region' && !el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby');
  }

  function initOne(item) {
    if (item.done) return;
    item.done = true;
    if (io) io.unobserve(item.el);
    var C = window.ClrzApp;
    var saved = {};
    var k;
    for (k in C) { if (Object.prototype.hasOwnProperty.call(C, k) && Array.isArray(C[k])) saved[k] = C[k]; }
    try {
      runInit(function (s) { return s === item.sel ? [item.el] : []; });
    } catch (e) {
      logError(e);
    } finally {
      for (k in saved) { C[k] = saved[k].concat(Array.isArray(C[k]) ? C[k] : []); }
    }
    if (item.sel === '.js-slider' && unnamedRegion(item.el)) ping();
  }

  function remaining() {
    for (var i = 0; i < pending.length; i++) { if (!pending[i].done) return true; }
    return false;
  }

  function nextQueued() {
    while (queue.length) {
      var it = queue.shift();
      if (!it.done) return it;
    }
    return null;
  }

  function nextPending() {
    for (var i = 0; i < pending.length; i++) { if (!pending[i].done) return pending[i]; }
    return null;
  }

  function finish() {
    if (remaining()) return;
    if (io) { io.disconnect(); io = null; }
    if (listening) {
      listening = false;
      USER_EVENTS.forEach(function (t) { document.removeEventListener(t, onUserEvent, true); });
    }
  }

  function schedule() {
    var urgent = false;
    for (var i = 0; i < queue.length; i++) { if (!queue[i].done) { urgent = true; break; } }
    if (urgent && !soonScheduled) {
      soonScheduled = true;
      window.setTimeout(runSoon, 0);
    }
    if (idleMode && !idleScheduled && remaining()) {
      idleScheduled = true;
      if (window.requestIdleCallback) window.requestIdleCallback(runIdle, { timeout: 2000 });
      else window.setTimeout(runIdle, 200);
    }
  }

  // Un seul composant par tâche : à l'approche de l'écran...
  function runSoon() {
    soonScheduled = false;
    var it = nextQueued();
    if (it) initOne(it);
    schedule();
    finish();
  }

  // ... puis, après le chargement, le reste pendant le temps libre.
  function runIdle() {
    idleScheduled = false;
    var it = nextQueued() || nextPending();
    if (it) initOne(it);
    schedule();
    finish();
  }

  function onUserEvent(e) {
    var t = e.target;
    if (!t || !t.nodeType) return;
    for (var i = 0; i < pending.length; i++) {
      if (!pending[i].done && pending[i].el.contains(t)) initOne(pending[i]);
    }
    finish();
  }

  function marquees() {
    var C = window.ClrzApp;
    var all = [].concat(C.AnnounceBarSlider || [], C.FooterSlider || []);
    var items = [];
    all.forEach(function (comp) {
      var sp = comp && comp.splide;
      var auto = sp && sp.Components && sp.Components.AutoScroll;
      if (!comp || !comp.el || !auto || typeof auto.pause !== 'function' || typeof auto.play !== 'function' ||
        typeof auto.isPaused !== 'function') return;
      items.push({ el: comp.el, auto: auto, off: false, mine: false });
    });
    if (!items.length) return;

    // Pause voulue par le visiteur : bouton pause (refonte-a11y.js), survol ou focus dans le bandeau.
    function held(it) {
      var tg = it.el.querySelector('[data-sc-marquee-toggle]');
      if (tg && tg.getAttribute('aria-pressed') === 'true') return true;
      var a = document.activeElement;
      if (a && a !== document.body && it.el.contains(a)) return true;
      try { return it.el.matches(':hover'); } catch (e) { return false; }
    }

    var mio = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          if (it.el !== entry.target) continue;
          it.off = !entry.isIntersecting;
          if (it.off) {
            if (!it.auto.isPaused()) { it.auto.pause(); it.mine = true; }
          } else if (it.mine) {
            it.mine = false;
            if (!held(it)) it.auto.play();
          }
        }
      });
    }, { rootMargin: MARQUEE_MARGIN });
    items.forEach(function (it) { mio.observe(it.el); });

    // AutoScroll se relance seul à l'événement load (autoStart) : on réapplique aussitôt les pauses.
    window.addEventListener('load', function () {
      items.forEach(function (it) {
        try {
          if (it.off) {
            if (!it.auto.isPaused()) it.auto.pause();
            it.mine = true;
          } else if (held(it) && !it.auto.isPaused()) {
            it.auto.pause();
          }
        } catch (e) {
          logError(e);
        }
      });
    });
  }

  window.scClrzLazyInit = function () {
    var C = window.ClrzApp;
    if (!C || typeof C.init !== 'function') return;
    if (!('IntersectionObserver' in window)) { C.init(); return; }

    try {
      runInit(function (s, read) {
        var list = read();
        if (LAZY.indexOf(s) === -1) return list;
        var keep = [];
        for (var i = 0; i < list.length; i++) {
          if (farBelow(list[i])) pending.push({ sel: s, el: list[i], done: false });
          else keep.push(list[i]);
        }
        return keep;
      });
    } catch (e) {
      // Erreur pendant l'appel filtré : les composants différés sont construits tout de suite (comportement d'origine).
      logError(e);
      pending.forEach(initOne);
      return;
    }

    try { marquees(); } catch (e) { logError(e); }

    if (!pending.length) return;

    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        for (var i = 0; i < pending.length; i++) {
          if (pending[i].el === entry.target && !pending[i].done) queue.push(pending[i]);
        }
      });
      schedule();
    }, { rootMargin: AHEAD });
    pending.forEach(function (item) { io.observe(item.el); });

    listening = true;
    USER_EVENTS.forEach(function (t) { document.addEventListener(t, onUserEvent, { capture: true, passive: true }); });

    function start() { window.setTimeout(function () { idleMode = true; schedule(); }, 1000); }
    if (document.readyState === 'complete') start(); else window.addEventListener('load', start);
  };
})();
